<template>
  <section class="section">
    <div class="container">
      <Title>
        Applications
        <HeaderIcon
          v-if="!downloading"
          @click="download()"
          icon="mdi-file-download"
          tooltip="Download CSV"
        ></HeaderIcon>
        <span v-if="downloading" class="spinner small"></span>
      </Title>

      <SearchBar
        v-model="tempFilter"
        :id="searchId"
        :selectOnSubmit="true"
        :loading="searching"
        :focus="true"
        @submit="search($event)"
      ></SearchBar>

      <Tips
        v-if="firstLoadDone && items.length !== 0"
        path="computerApplications"
        xKey="dismissedTips"
        property="rightClick"
        :rightClickIconVisible="true"
        >Right-click on a license cell to open the context menu
      </Tips>

      <LoaderOne v-show="!firstLoadDone"></LoaderOne>

      <p v-show="!searching && firstLoadDone && items.length === 0">
        <i>No applications.</i>
      </p>

      <table
        class="table is-hoverable is-fullwidth is-narrow"
        v-if="firstLoadDone && items.length !== 0"
      >
        <thead>
          <tr class="is-unselectable">
            <th @click="handleOrder('name')">Name</th>
            <th @click="handleOrder('version')">Version</th>
            <th @click="handleOrder('key')">License</th>
            <th @click="handleOrder('installed')">Installed</th>
            <th @click="handleOrder('timeChanged')">When</th>
          </tr>
        </thead>
        <TableEffect :skipGroupTransition="true">
          <tr v-for="item of filtered" :key="key(item)">
            <td>{{ item.name }}</td>
            <td>{{ item.version }}</td>
            <td @contextmenu.prevent="$refs.menu.open($event, item)">
              <span
                v-if="!item.edit && item.key && item.key !== ''"
                class="clickable"
                >{{ item.key }}</span
              >
              <span
                v-if="!item.edit && (!item.key || item.key === '')"
                class="clickable"
              >
                <i>No license</i>
              </span>

              <EditSingleField
                v-if="item.edit"
                :text="item.key"
                validation
                type="text"
                name="name"
                @save="rename(item, $event)"
                @cancel="toggleRename(item)"
                :isLoading="saving"
              ></EditSingleField>
            </td>
            <td>
              <span class="icon">
                <i
                  class="mdi"
                  :class="{
                    'mdi-check has-text-success': item.installed,
                    'mdi-delete has-text-grey': !item.installed,
                  }"
                ></i>
              </span>
            </td>
            <td>
              <span v-if="item.timeChanged">
                {{ date(item.timeChanged) }}
              </span>
              <span v-else>
                <i>Unknown</i>
              </span>
            </td>
          </tr>
        </TableEffect>
      </table>
      <Paging
        v-if="items && items.length > 0"
        :items="paging.pages"
        :selected="paging.page"
        @change="setPage($event)"
        @size="setPageMax($event)"
        :size="paging.max"
        :hasOptions="true"
        autosave="computerApplications"
      ></Paging>
    </div>

    <vue-context ref="menu">
      <template slot-scope="child">
        <li>
          <a @click.prevent="toggleRename(child.data)">Edit</a>
        </li>
      </template>
    </vue-context>
  </section>
</template>

<script>
import "vue-context/src/sass/vue-context.scss";
import { getNumbers, getStartIndex } from "@/utils/paging";

import { fromNow, toReadableDate } from "@/utils/date";

import { postApi } from "@/utils/http";
import { focus, getId, downloadFile, getTextUrl } from "@/utils/document";
import { _order } from "@/utils/order";

export default {
  components: {
    TableEffect: () => import("@/components/singles/TableEffect.vue"),
    Tips: () => import("@/components/main/Tips.vue"),
    Title: () => import("@/components/singles/Title.vue"),
    SearchBar: () => import("@/components/main/SearchBar.vue"),
    HeaderIcon: () => import("@/components/main/HeaderIcon.vue"),
    Paging: () => import("@/components/main/paging/Nav.vue"),
    EditSingleField: () =>
      import("@/components/singles/EditSingleField/component.vue"),
    LoaderOne: () => import("@/components/singles/LoaderOne.vue"),
    VueContext: () => import("vue-context"),
  },

  computed: {
    date() {
      return (date) => {
        return toReadableDate(date);
      };
    },
    filtered() {
      return this.items.slice(
        this.paging.start,
        this.paging.page * this.paging.max
      );
    },
    key() {
      return (item) => {
        return item.name + item.version + item.installed + item.id;
      };
    },
  },
  data() {
    return {
      items: [],
      allItems: [],
      firstLoadDone: false,
      downloading: false,
      searching: false,
      tempFilter: "",
      filter: "",
      saving: false,
      searchId: getId(),
      paging: {
        page: 1,
        max: 15,
        start: 0,
        pages: [],
      },
    };
  },
  async created() {
    try {
      await this.getApplications();

      this.firstLoadDone = true;
    } catch (err) {
      console.log(err);
      this.$helpers.notify("Error", "Could not get data.", "danger", 5000);
    }
  },
  beforeDestroy() {},
  methods: {
    handleOrder(path) {
      this.items = this.order(this.items, path);
    },
    order(items, path) {
      const stateOrder = this.$store.state.settings.user.computerApplications
        ?.order;
      let reversed = false;

      if (stateOrder?.path === path) reversed = !stateOrder.reversed;

      this.$store.dispatch("settings/setUser", {
        path: "computerApplications",
        key: "order",
        value: { path, reversed },
      });

      return _order(items, { path, reversed });
    },
    setPageMax(size) {
      this.paging.max = size;

      this.setPage(1);
      this.setPages(this.items);
    },
    setPage(page) {
      this.paging.start = getStartIndex(page, this.paging.max);
      this.paging.page = page;
    },
    focus: focus,
    async getApplications() {
      this.searching = true;

      await this.$store.dispatch("settings/getUserSetting", {
        path: "computerApplications",
        key: "order",
      });

      const stateOrder = this.$store.state.settings.user.computerApplications
        ?.order;

      const res = await postApi("get/computerApplications", {
        domainUID: this.$route.params.domainUID,
        computerUIDs: [this.$route.params.computerUID],
      });

      const paging = this.$store.state.settings.user.computerApplications
        ?.paging;
      if (paging && paging.max) this.paging.max = paging.max;

      this.setPages(res[0].applications);
      this.items = _order(res[0].applications, stateOrder);
      this.allItems = res[0].applications;

      this.searching = false;
      focus(this.searchId);
    },

    setPages(items) {
      this.paging.pages = getNumbers(items, this.paging.max);
    },

    async search(searchStr) {
      this.searching = true;

      const stateOrder = this.$store.state.settings.user.computerApplications
        ?.order;

      searchStr = searchStr.trim().toLowerCase();
      this.filter = searchStr;

      if (searchStr != "") {
        this.items = this.order(
          this.allItems.filter(
            (x) =>
              x.name.toLowerCase().includes(searchStr) ||
              x.version?.includes(searchStr) ||
              x.license?.toLowerCase().includes(searchStr)
          ),
          stateOrder
        );
      } else this.items = this.order(this.allItems, stateOrder);

      this.setPages(this.items);
      this.setPage(1);

      this.searching = false;
      focus(this.searchId);
    },
    toggleRename(item) {
      const index = this.items.findIndex((x) => item == x);
      this.$set(this.items, index, { ...item, ...{ edit: !item.edit } });
    },
    async rename(item, text) {
      this.saving = true;
      text = text.trim();

      await postApi("set/applicationLicense", {
        domainUID: this.$route.params.domainUID,
        computerUID: this.$route.params.computerUID,
        name: item.name,
        key: text,
        version: item.version,
      });

      const index = this.items.findIndex((x) => item == x);
      const newItem = { ...item, ...{ key: text } };
      this.$set(this.items, index, newItem);
      this.toggleRename(newItem);

      this.saving = false;
    },
    download() {
      this.downloading = true;
      let text = `Name;Version;License;Installed;Date\r\n`;

      this.items
        .filter((x) => x)
        .map(
          (x) =>
            (text += `${x?.name || ""};${x?.version || ""};${x?.key || ""};${
              x.installed ? "Yes" : "No"
            };${x.timeChanged ? toReadableDate(x.timeChanged) : ""};\r\n`)
        );

      const computer = this.$store.getters["computer/singleComputer"](
        this.$route.params.computerUID
      );

      let computerName = "";
      if (computer) computerName = computer.name + "_";

      downloadFile(getTextUrl(text), `${computerName}applications.csv`);
      this.downloading = false;
    },
  },
};
</script>
